import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { TIERS } from "../../constants/membership";
import MMPreferenceSegmentRow from "./MMPreferenceSegmentRow";
import MMPreferenceToggleRow from "./MMPreferenceToggleRow";
import {
    AGE_OPTIONS,
    ELO_GAP_OPTIONS,
    MATCH_COUNT_OPTIONS,
    PLAYTIME_OPTIONS,
    resolveSegmentFigure,
} from "./mmPreferenceSegments";
import PremiumOnlyOverlay from "./PremiumOnlyOverlay";
import { shouldShowPremiumMatchFiltersPrompt } from "./premiumMatchFiltersGate";
import {
    SETTINGS_CONTENT_COLUMN_SX,
    SETTINGS_FULL_DIVIDER_SX,
    SETTINGS_SECTION_DESC_SX,
    SETTINGS_SECTION_PAD_SX,
    SETTINGS_SECTION_TITLE_SX,
} from "./lobbySettingsStyles";

const settingsConfig = {
    experienceRequirements: [
        {
            id: "setting_01",
            name: "above_hours",
            title: "Minimum playtime required",
            icon: "AccessTime",
            toggleState: false,
            figure: 0,
            hasSegments: true,
            options: PLAYTIME_OPTIONS,
        },
        {
            id: "setting_02",
            name: "total_steam_cs_matches_played",
            title: "Minimum Valve MM matches",
            icon: "SportsEsports",
            toggleState: false,
            figure: 0,
            hasSegments: true,
            options: MATCH_COUNT_OPTIONS,
        },
        {
            id: "setting_03",
            name: "total_faceit_matches_played",
            title: "Minimum FaceIT matches",
            icon: "SportsEsports",
            toggleState: false,
            figure: 0,
            hasSegments: true,
            options: MATCH_COUNT_OPTIONS,
        },
    ],
    skillRestrictions: [
        {
            id: "setting_04",
            name: "maxEloDifferenceInTeam",
            title: "Maximum ELO gap between players",
            icon: "BubbleChart",
            toggleState: true,
            figure: 500,
            hasSegments: true,
            options: ELO_GAP_OPTIONS,
        },
        {
            id: "setting_05",
            name: "min_player_age",
            title: "Minimum Age Requirement",
            icon: "Person",
            toggleState: false,
            figure: 0,
            hasSegments: true,
            options: AGE_OPTIONS,
        },
    ],
    inventoryChecks: [
        {
            id: "setting_06",
            name: "player_has_inventory",
            title: "Match players with an inventory",
            icon: "LocalMall",
            toggleState: false,
            hasSegments: false,
        },
        {
            id: "setting_07",
            name: "player_has_expensive_inventory",
            title: "Minimum Inventory Value ($500+)",
            icon: "AttachMoney",
            toggleState: false,
            hasSegments: false,
        },
    ],
    playerFilters: [
        {
            id: "setting_08",
            name: "premium_or_pro_only",
            title: "Premium or Pro players only",
            icon: "WorkspacePremium",
            toggleState: false,
            hasSegments: false,
        },
        {
            id: "setting_09",
            name: "biometric_verified_only",
            title: "Biometrically verified players only",
            icon: "Fingerprint",
            toggleState: false,
            hasSegments: false,
        },
        {
            id: "setting_10",
            name: "prefer_female_teammates_if_possible",
            title: "Prioritize playing with other women if possible",
            icon: "Female",
            toggleState: false,
            hasSegments: false,
            femaleOnly: true,
        },
    ],
};

function settingsFingerprint(settings) {
    return [
        ...settings.experienceRequirements,
        ...settings.skillRestrictions,
        ...settings.inventoryChecks,
        ...settings.playerFilters,
    ]
        .map((setting) => `${setting.id}:${setting.toggleState ? 1 : 0}:${setting.figure ?? ""}`)
        .join("|");
}

function initializeSettingsFromOptions(options) {
    return {
        experienceRequirements: settingsConfig.experienceRequirements.map(
            (setting) => {
                const value = resolveSegmentFigure(options, setting);
                return {
                    ...setting,
                    figure: value,
                    toggleState: value > 0,
                };
            },
        ),
        skillRestrictions: settingsConfig.skillRestrictions.map((setting) => {
            const value = resolveSegmentFigure(options, setting);
            return {
                ...setting,
                figure: value,
                toggleState: value > 0,
            };
        }),
        inventoryChecks: settingsConfig.inventoryChecks.map((setting) => {
            const isEnabled = options[setting.name] || false;
            return {
                ...setting,
                toggleState: isEnabled,
            };
        }),
        playerFilters: settingsConfig.playerFilters.map((setting) => {
            const isEnabled = options[setting.name] || false;
            return {
                ...setting,
                toggleState: isEnabled,
            };
        }),
    };
}

const settingsGridSx = {
    width: "100%",
    mt: "0.5rem",
};

function renderSettingsGrid(settings, { onFigureChange, disabled }) {
    return (
        <Box sx={settingsGridSx}>
            {settings.map((setting) => (
                <MMPreferenceSegmentRow
                    key={setting.id}
                    setting={setting}
                    onFigureChange={onFigureChange}
                    disabled={disabled}
                />
            ))}
        </Box>
    );
}

const toggleRowsSx = {
    width: "100%",
    mt: "0.5rem",
};

function renderToggleRows(settings, { onToggleChange, disabled }) {
    return (
        <Box sx={toggleRowsSx}>
            {settings.map((setting) => (
                <MMPreferenceToggleRow
                    key={setting.id}
                    setting={setting}
                    onToggleChange={onToggleChange}
                    disabled={disabled}
                />
            ))}
        </Box>
    );
}

export default function MMPreferencesTab({
    matchmakingOptions = {},
    onMatchmakingOptionChange,
    onLeaderMatchmakingUpdate,
    canEditLobbySettings = false,
    currentLobbyCode,
    isMatchmakingEditingDisabled,
    currentUserGender = null,
    settingsResetVersion = 0,
    membershipTier = TIERS.FREE,
    hidePremiumOverlay = false,
}) {
    const canUseFemalePreference = currentUserGender === "female";

    const [localSettings, setLocalSettings] = useState(() =>
        initializeSettingsFromOptions(matchmakingOptions),
    );

    const lastLobbyCode = useRef(currentLobbyCode);
    const lastOptionsRef = useRef(matchmakingOptions);
    const lastSettingsResetVersionRef = useRef(settingsResetVersion);

    useEffect(() => {
        const lobbyChanged = lastLobbyCode.current !== currentLobbyCode;
        const optionsChanged = lastOptionsRef.current !== matchmakingOptions;

        if (!lobbyChanged && !optionsChanged) {
            return;
        }

        lastLobbyCode.current = currentLobbyCode;
        lastOptionsRef.current = matchmakingOptions;

        const incoming = initializeSettingsFromOptions(matchmakingOptions);
        if (lobbyChanged) {
            setLocalSettings(incoming);
            return;
        }

        setLocalSettings((prev) =>
            settingsFingerprint(prev) === settingsFingerprint(incoming)
                ? prev
                : incoming,
        );
    }, [matchmakingOptions, currentLobbyCode]);

    useEffect(() => {
        if (lastSettingsResetVersionRef.current === settingsResetVersion) {
            return;
        }
        setLocalSettings(initializeSettingsFromOptions(matchmakingOptions));
        lastSettingsResetVersionRef.current = settingsResetVersion;
    }, [settingsResetVersion, matchmakingOptions]);

    const handleToggleChange = (settingId, newToggleState) => {
        const allSettings = [
            ...localSettings.experienceRequirements,
            ...localSettings.skillRestrictions,
            ...localSettings.inventoryChecks,
            ...localSettings.playerFilters,
        ];
        const currentSetting = allSettings.find((s) => s.id === settingId);

        const updatedSettings = {
            experienceRequirements: localSettings.experienceRequirements.map(
                (setting) =>
                    setting.id === settingId
                        ? { ...setting, toggleState: newToggleState }
                        : setting,
            ),
            skillRestrictions: localSettings.skillRestrictions.map((setting) =>
                setting.id === settingId
                    ? { ...setting, toggleState: newToggleState }
                    : setting,
            ),
            inventoryChecks: localSettings.inventoryChecks.map((setting) =>
                setting.id === settingId
                    ? { ...setting, toggleState: newToggleState }
                    : setting,
            ),
            playerFilters: localSettings.playerFilters.map((setting) =>
                setting.id === settingId
                    ? { ...setting, toggleState: newToggleState }
                    : setting,
            ),
        };
        setLocalSettings(updatedSettings);

        if (currentSetting) {
            const settingData = {
                name: currentSetting.name,
                enabled: newToggleState,
                value: currentSetting.hasSegments
                    ? currentSetting.figure
                    : undefined,
                hasSlider: currentSetting.hasSegments,
            };

            if (
                canEditLobbySettings &&
                currentLobbyCode &&
                onLeaderMatchmakingUpdate
            ) {
                onLeaderMatchmakingUpdate(settingData);
            } else if (onMatchmakingOptionChange) {
                onMatchmakingOptionChange(settingData);
            }
        }
    };

    const handleFigureChange = (settingId, nextFigure) => {
        const updatedSettings = {
            experienceRequirements: localSettings.experienceRequirements.map(
                (setting) =>
                    setting.id === settingId
                        ? {
                              ...setting,
                              figure: nextFigure,
                              toggleState: nextFigure > 0,
                          }
                        : setting,
            ),
            skillRestrictions: localSettings.skillRestrictions.map((setting) =>
                setting.id === settingId
                    ? {
                          ...setting,
                          figure: nextFigure,
                          toggleState: nextFigure > 0,
                      }
                    : setting,
            ),
            inventoryChecks: localSettings.inventoryChecks.map((setting) =>
                setting.id === settingId
                    ? { ...setting, figure: nextFigure }
                    : setting,
            ),
            playerFilters: localSettings.playerFilters.map((setting) =>
                setting.id === settingId
                    ? { ...setting, figure: nextFigure }
                    : setting,
            ),
        };
        setLocalSettings(updatedSettings);

        const updatedSetting = [
            ...updatedSettings.experienceRequirements,
            ...updatedSettings.skillRestrictions,
            ...updatedSettings.inventoryChecks,
        ].find((s) => s.id === settingId);

        if (updatedSetting) {
            const settingData = {
                name: updatedSetting.name,
                enabled: nextFigure > 0,
                value: nextFigure,
                hasSlider: true,
            };

            requestAnimationFrame(() => {
                if (
                    canEditLobbySettings &&
                    currentLobbyCode &&
                    onLeaderMatchmakingUpdate
                ) {
                    onLeaderMatchmakingUpdate(settingData);
                } else if (onMatchmakingOptionChange) {
                    onMatchmakingOptionChange(settingData);
                }
            });
        }
    };

    const playerFiltersVisible = localSettings.playerFilters.filter(
        (setting) => !setting.femaleOnly || canUseFemalePreference,
    );

    const showPremiumGate =
        shouldShowPremiumMatchFiltersPrompt({
            membershipTier,
            currentLobbyCode,
            canEditLobbySettings,
        }) && !hidePremiumOverlay;

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                sx={{
                    ...(showPremiumGate && {
                        opacity: 0.4,
                        pointerEvents: "none",
                        userSelect: "none",
                    }),
                }}
            >
                <Box sx={SETTINGS_SECTION_PAD_SX}>
                    <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                            }}
                        >
                            <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                                Experience requirements
                            </Typography>
                            <Typography sx={SETTINGS_SECTION_DESC_SX}>
                                Filter by playtime and platform match history.
                            </Typography>
                        </Box>
                        {renderSettingsGrid(localSettings.experienceRequirements, {
                            onFigureChange: handleFigureChange,
                            disabled: isMatchmakingEditingDisabled,
                        })}
                    </Box>
                </Box>

                <Box sx={SETTINGS_FULL_DIVIDER_SX} />

                <Box sx={SETTINGS_SECTION_PAD_SX}>
                    <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                            }}
                        >
                            <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                                Skill restrictions
                            </Typography>
                            <Typography sx={SETTINGS_SECTION_DESC_SX}>
                                Keep teams closer in age and rating range.
                            </Typography>
                        </Box>
                        {renderSettingsGrid(localSettings.skillRestrictions, {
                            onFigureChange: handleFigureChange,
                            disabled: isMatchmakingEditingDisabled,
                        })}
                    </Box>
                </Box>

                <Box sx={SETTINGS_FULL_DIVIDER_SX} />

                <Box sx={SETTINGS_SECTION_PAD_SX}>
                    <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                            }}
                        >
                            <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                                Inventory checks
                            </Typography>
                            <Typography sx={SETTINGS_SECTION_DESC_SX}>
                                Prefer players with an inventory signal.
                            </Typography>
                        </Box>
                        {renderToggleRows(localSettings.inventoryChecks, {
                            onToggleChange: handleToggleChange,
                            disabled: isMatchmakingEditingDisabled,
                        })}
                    </Box>
                </Box>

                <Box sx={SETTINGS_FULL_DIVIDER_SX} />

                <Box sx={SETTINGS_SECTION_PAD_SX}>
                    <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.25rem",
                            }}
                        >
                            <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                                Player quality
                            </Typography>
                            <Typography sx={SETTINGS_SECTION_DESC_SX}>
                                Limit the queue to verified or paid accounts.
                            </Typography>
                        </Box>
                        {renderToggleRows(playerFiltersVisible, {
                            onToggleChange: handleToggleChange,
                            disabled: isMatchmakingEditingDisabled,
                        })}
                    </Box>
                </Box>
            </Box>
            {showPremiumGate ? (
                <PremiumOnlyOverlay
                    title="Control who you queue with"
                    description="Keep smurfs, wide ELO gaps, and random regions out of your search."
                />
            ) : null}
        </Box>
    );
}